import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface SaleRow {
  id: string;
  item_id: string;
  sold_price: number | string | null;
  platform: string | null;
  sold_at: string;
}

/**
 * Closes A5 in docs/UX_ANALYSIS.md — silent sale arrivals.
 *
 * Subscribes ONCE (when the AppShell mounts) to `postgres_changes`
 * INSERTs on `public.sales`. RLS scopes the channel to rows the
 * authenticated user can see (sales policy joins through
 * inventory_items.user_id = auth.uid()), so we don't need an extra
 * filter on this side.
 *
 * For each new row, we:
 *   1. Resolve the inventory item's name via a single .select() so
 *      the toast can show what sold, not just a UUID.
 *   2. Invalidate the dashboard + inventory query caches so the
 *      KPIs and the row's status update without a manual refresh.
 *   3. Fire a sonner toast with a click action navigating to
 *      `/transactions` for the full receipt.
 *
 * Failure mode: if Supabase realtime isn't reachable (or the row's
 * inventory_items lookup fails), we degrade silently. The user still
 * sees the sale on next page load via the existing useQuery polls.
 */
export function useSaleArrivalToast() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel(`sales-arrival-${crypto.randomUUID()}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'sales' },
        async (payload) => {
          const row = payload.new as SaleRow | undefined;
          if (!row?.id || !row?.item_id) return;

          // Best-effort name resolution. If the inventory item was
          // somehow deleted between insert and lookup, fall back to a
          // generic message rather than swallowing the toast.
          let itemName = 'an item';
          try {
            const { data } = await supabase
              .from('inventory_items')
              .select('name')
              .eq('id', row.item_id)
              .maybeSingle();
            const name = (data as { name: string } | null)?.name;
            if (name && name.trim().length > 0) itemName = name;
          } catch {
            /* network/RLS hiccup — fall back to generic */
          }

          // Refresh caches so badges + KPIs reflect the sale.
          queryClient.invalidateQueries({ queryKey: ['dashboard_metrics'] });
          queryClient.invalidateQueries({ queryKey: ['inventory'] });
          queryClient.invalidateQueries({ queryKey: ['active_ebay_listings'] });

          const priceNum = Number(row.sold_price ?? 0);
          const priceStr = Number.isFinite(priceNum) ? `$${priceNum.toFixed(2)}` : '—';

          toast.success(`New sale: ${itemName}`, {
            description: `${priceStr}${row.platform ? ` on ${row.platform}` : ''}`,
            duration: 8_000,
            action: {
              label: 'View',
              onClick: () => navigate('/transactions'),
            },
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [navigate, queryClient]);
}
